import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Tabs, Tab, Spinner } from 'react-bootstrap';
import { emailTemplateService } from '../../services/api/emailTemplateService';
import { candidateService } from '../../services/api/candidateService';
import { Candidate } from '../../types/types';

interface EmailPreviewModalProps {
    show: boolean;
    onHide: () => void;
    templateId: string;
    templateName: string;
}

const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
    show,
    onHide,
    templateId,
    templateName
}) => {
    const [previewHtml, setPreviewHtml] = useState<string>('');
    const [originalTemplateHtml, setOriginalTemplateHtml] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<string>('sample');
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [selectedCandidateId, setSelectedCandidateId] = useState<string>('');
    const [customMessage, setCustomMessage] = useState<string>('');
    const [loadingCandidates, setLoadingCandidates] = useState<boolean>(false);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (show) {
            setPreviewHtml('');
            setOriginalTemplateHtml('');
            setSelectedCandidateId('');
            setCustomMessage('');
            setError(null);
            setActiveTab('sample');
            // Load initial preview
            loadTemplatePreview();
        }
    }, [show, templateId]);

    // Update preview when custom message changes
    useEffect(() => {
        if (originalTemplateHtml) {
            updatePreviewWithCustomMessage();
        }
    }, [customMessage]);

    const loadTemplatePreview = async (candidateId?: string) => {
        if (!templateId) return;

        try {
            setLoading(true);
            setError(null);

            const html = await emailTemplateService.previewEmail(templateId, candidateId);
            setOriginalTemplateHtml(html);
            updatePreviewWithCustomMessage(html);
        } catch (err) {
            console.error('Error loading template preview:', err);
            setError('Failed to load email preview.');
        } finally {
            setLoading(false);
        }
    };

    const updatePreviewWithCustomMessage = (htmlToUse?: string) => {
        const baseHtml = htmlToUse || originalTemplateHtml;
        if (!baseHtml) return;

        // Always replace the placeholder, whether customMessage is empty or not
        let updatedHtml = baseHtml.replace(/\{\{CustomMessage\}\}/g, customMessage || '');

        setPreviewHtml(updatedHtml);
    };

    const fetchCandidates = async () => {
        if (candidates.length > 0) return;

        try {
            setLoadingCandidates(true);
            const response = await candidateService.getCandidates();
            setCandidates(response.items || []);
        } catch (err) {
            console.error('Failed to fetch candidates:', err);
            setError('Failed to load candidates.');
        } finally {
            setLoadingCandidates(false);
        }
    };

    const handleTabChange = (tab: string | null) => {
        if (tab) {
            setActiveTab(tab);

            if (tab === 'real') {
                fetchCandidates();
            }

            // If switching back to sample and we have a candidate selected, reload with sample data
            if (tab === 'sample' && selectedCandidateId) {
                loadTemplatePreview(); // Load without candidate ID for sample data
            }
        }
    };

    const handleCandidateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const candidateId = e.target.value;
        setSelectedCandidateId(candidateId);

        if (candidateId) {
            loadTemplatePreview(candidateId);
        } else {
            // No candidate selected, show template with sample data
            loadTemplatePreview();
        }
    };

    const handleCustomMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setCustomMessage(e.target.value);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-envelope-paper me-2"></i>
                    Preview: {templateName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Tabs
                    activeKey={activeTab}
                    onSelect={handleTabChange}
                    className="mb-3"
                >
                    <Tab eventKey="sample" title="Sample Data">
                        <p className="text-muted mb-3">
                            This preview uses sample placeholder data to show how your email will look.
                        </p>
                    </Tab>
                    <Tab eventKey="real" title="Real Data Preview">
                        <Form.Group className="mb-3">
                            <Form.Label>Select Candidate</Form.Label>
                            {loadingCandidates ? (
                                <div className="text-center py-2">
                                    <Spinner animation="border" size="sm" /> Loading candidates...
                                </div>
                            ) : (
                                <Form.Select
                                    value={selectedCandidateId}
                                    onChange={handleCandidateChange}
                                >
                                    <option value="">Select a candidate</option>
                                    {candidates.map(candidate => (
                                        <option key={candidate.id} value={candidate.id}>
                                            {candidate.firstName} {candidate.lastName} - {candidate.jobTitle || 'No job'}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                            <Form.Text className="text-muted">
                                Select a candidate to see how the email will appear with their actual data.
                            </Form.Text>
                        </Form.Group>
                    </Tab>
                </Tabs>

                <Form.Group className="mb-3">
                    <Form.Label>Custom Message (will replace {'{{CustomMessage}}'} placeholder)</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={customMessage}
                        onChange={handleCustomMessageChange}
                        placeholder="Enter your custom message for the email"
                    />
                </Form.Group>

                <hr />

                <h5>Email Preview</h5>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Generating preview...</p>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger">{error}</div>
                ) : (
                    <div
                        className="email-preview border p-3 rounded bg-light"
                        style={{ minHeight: '300px', overflow: 'auto' }}
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                )}

                <div className="text-muted small mt-2">
                    {activeTab === 'sample'
                        ? 'Note: This is a preview with sample data. Actual emails will be personalized with candidate information.'
                        : selectedCandidateId
                            ? 'This preview shows how the email will appear to the selected candidate.'
                            : 'Select a candidate above to preview with real data.'}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EmailPreviewModal;